/**
 * Tab-scoped shopping-session attempt key (personalize + `/checkout` refresh).
 * Used by `plugins/shopping-session-persist.client.ts` and clear sites in the store.
 */
import type { CheckoutAddressesData, CheckoutJourney } from '~~/layers/base/types/checkout'

export const SHOPPING_SESSION_PERSIST_KEY = 'shoppingSession.attempt'

/**
 * Snapshot written to `sessionStorage` for in-tab reload recovery.
 * - Ready **addresses** for Recipient / Return after `/checkout` reload (TK-12805)
 * - Confirmed Stripe `client_secret` so remount can skip a second `confirmPayment` (TK-12803)
 * Does **not** persist card UI completeness flags or other segment ready data.
 */
export type ShoppingSessionPersistSnapshot = {
    journey: CheckoutJourney | null
    designProjectId: string | null
    handwritingProjectId: string | null
    handwritingStationeryId: string | null
    shoppingSessionId: string | null
    cartId: string | null
    /** Ready address cards only — omitted / null when not ready. */
    addresses?: CheckoutAddressesData | null
    /** Confirmed PaymentIntent client_secret — null/omit when unset. */
    stripeClientSecret?: string | null
}

/** Minimal Pinia state slice read/written by shopping-session persist. */
export type ShoppingSessionPersistStateSlice = {
    journey?: CheckoutJourney | null
    designProjectId?: string | null
    handwritingProjectId?: string | null
    handwritingStationeryId?: string | null
    shoppingSessionId?: string | null
    stripeClientSecret?: string | null
    cart?: { data?: { cartId?: string | null } } | null
    addresses?: { status?: string; data?: CheckoutAddressesData } | null
}

export function clearShoppingSessionPersist(): void {
    if (typeof sessionStorage === 'undefined') {
        return
    }
    sessionStorage.removeItem(SHOPPING_SESSION_PERSIST_KEY)
}

/** Ready address cards only — null when the segment is not ready or empty. */
export function addressesForPersistSnapshot(
    addresses: ShoppingSessionPersistStateSlice['addresses']
): CheckoutAddressesData | null {
    if (addresses?.status !== 'ready' || !addresses.data) {
        return null
    }
    const { shippingAddress, billingAddress } = addresses.data
    if (!shippingAddress && !billingAddress) {
        return null
    }
    return { shippingAddress: shippingAddress ?? null, billingAddress: billingAddress ?? null }
}

/** Build the persist snapshot from Pinia shopping-session state. */
export function buildShoppingSessionPersistSnapshot(
    state: ShoppingSessionPersistStateSlice
): ShoppingSessionPersistSnapshot {
    const cart = state.cart
    const secret = state.stripeClientSecret?.trim() || null
    return {
        journey: state.journey ?? null,
        designProjectId: state.designProjectId ?? null,
        handwritingProjectId: state.handwritingProjectId ?? null,
        handwritingStationeryId: state.handwritingStationeryId ?? null,
        shoppingSessionId: state.shoppingSessionId ?? null,
        cartId: cart?.data?.cartId ?? null,
        addresses: addressesForPersistSnapshot(state.addresses),
        stripeClientSecret: secret,
    }
}

/**
 * Parse a raw `sessionStorage` JSON string into a snapshot.
 * Returns null when empty or invalid (caller should clear storage).
 */
export function parseShoppingSessionPersistSnapshot(
    raw: string | null | undefined
): ShoppingSessionPersistSnapshot | null {
    const trimmed = raw?.trim()
    if (!trimmed) {
        return null
    }
    try {
        return JSON.parse(trimmed) as ShoppingSessionPersistSnapshot
    } catch {
        return null
    }
}
